import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDoctorAppointments } from "../../services/api";
import {
    startOfMonth, endOfMonth, startOfWeek, endOfWeek,
    addDays, format, isSameMonth, isSameDay,
    addMonths, subMonths, parseISO
} from "date-fns";
import "./appointments.css";

export default function DoctorCalendar() {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
    const [currentMonth, setCurrentMonth] = useState(new Date());

    useEffect(() => {
        fetchDoctorAppointments()
            .then(data => {
                setAppointments(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError("Failed to load schedule");
                setLoading(false);
            });
    }, []);

    if (loading) return <div style={{ padding: 20 }}>Loading schedule...</div>;
    if (error) return <div style={{ padding: 20, color: 'red' }}>{error}</div>;

    return (
        <div className="appts-page">
            <div className="appts-head">
                <h1>My Schedule</h1>
                <div className="view-toggle">
                    <button
                        className={viewMode === 'calendar' ? 'active' : ''}
                        onClick={() => setViewMode('calendar')}
                    >
                        Calendar
                    </button>
                    <button
                        className={viewMode === 'list' ? 'active' : ''}
                        onClick={() => setViewMode('list')}
                    >
                        List
                    </button>
                </div>
            </div>

            {viewMode === 'calendar' ? (
                <CalendarView
                    currentMonth={currentMonth}
                    setCurrentMonth={setCurrentMonth}
                    appointments={appointments}
                />
            ) : (
                <ListView appointments={appointments} />
            )}
        </div>
    );
}

function CalendarView({ currentMonth, setCurrentMonth, appointments }) {
    const renderHeader = () => {
        return (
            <div className="calendar-controls">
                <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>&lt;</button>
                <h2>{format(currentMonth, "MMMM yyyy")}</h2>
                <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>&gt;</button>
            </div>
        );
    };

    const renderDays = () => {
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return (
            <div className="calendar-grid header-row">
                {days.map((day, i) => (
                    <div className="calendar-header-cell" key={i}>{day}</div>
                ))}
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = "";

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, "d");
                const cloneDay = day;

                // Find appointments for this day
                const dayAppts = appointments.filter(appt =>
                    isSameDay(parseISO(appt.start_time), cloneDay)
                );

                days.push(
                    <div
                        className={`calendar-cell ${!isSameMonth(day, monthStart) ? "inactive" : ""
                            } ${isSameDay(day, new Date()) ? "today" : ""}`}
                        key={day}
                    >
                        <div className="date-header">
                            <span className="day-number">{formattedDate}</span>
                        </div>
                        <div className="events-container">
                            {dayAppts.map(appt => (
                                <Link
                                    to={`/fiche/${appt.id}`}
                                    key={appt.id}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <div className={`calendar-event ${appt.status}`}>
                                        {format(parseISO(appt.start_time), "HH:mm")} {appt.first_name}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                );
                day = addDays(day, 1);
            }
            rows.push(
                <div className="calendar-grid" key={day} style={{ borderTop: 'none', borderRadius: 0 }}>
                    {days}
                </div>
            );
            days = [];
        }

        // Slightly hacky grid render: just render all cells in one big grid instead of rows to match CSS
        // Re-rendering with single grid container logic for better responsiveness
        day = startDate;
        const allDays = [];
        while (day <= endDate) {
            const cloneDay = day;
            const dayAppts = appointments.filter(appt =>
                isSameDay(parseISO(appt.start_time), cloneDay)
            );

            allDays.push(
                <div
                    className={`calendar-cell ${!isSameMonth(day, monthStart) ? "inactive" : ""
                        } ${isSameDay(day, new Date()) ? "today" : ""}`}
                    key={day}
                >
                    <div className="date-header">
                        <span className="day-number">{format(day, "d")}</span>
                    </div>
                    {dayAppts.map(appt => (
                        <Link
                            to={`/fiche/${appt.id}`}
                            key={appt.id}
                            style={{ textDecoration: 'none' }}
                            title={`${appt.first_name} ${appt.last_name} - ${appt.type || 'Consultation'}`}
                        >
                            <div className={`calendar-event ${appt.status}`}>
                                {format(parseISO(appt.start_time), "HH:mm")} {appt.first_name} {appt.last_name.charAt(0)}.
                            </div>
                        </Link>
                    ))}
                </div>
            );
            day = addDays(day, 1);
        }

        return <div className="calendar-grid">{allDays}</div>;
    };

    return (
        <div>
            {renderHeader()}
            {renderDays()}
            {renderCells()}
        </div>
    );
}

function ListView({ appointments }) {
    // Reuse existing grouping logic
    const grouped = appointments.reduce((acc, curr) => {
        const date = new Date(curr.start_time).toLocaleDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(curr);
        return acc;
    }, {});

    if (Object.keys(grouped).length === 0) return <div className="empty">No appointments found.</div>;

    return (
        <div className="appts-list">
            {Object.keys(grouped).sort().map(date => (
                <div key={date}>
                    <h3 style={{ margin: '20px 0 10px', fontSize: 14, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>{date}</h3>
                    {grouped[date].map(appt => (
                        <div key={appt.id} className="appt-card">
                            <div className="info">
                                <h3>{new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {appt.first_name} {appt.last_name}</h3>
                                <p>{appt.type || "General Consultation"} • {appt.email}</p>
                            </div>
                            <span className={`tag ${appt.status}`}>{appt.status}</span>
                            <div className="actions">
                                <Link to={`/fiche/${appt.id}`} className="btn ghost" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                                    Open Fiche
                                </Link>
                                {appt.meet_link && <a href={appt.meet_link} target="_blank" rel="noreferrer" className="btn" style={{ background: '#4285f4' }}>Join</a>}
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

